import { DepthCard } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * Verification harness for the DepthCard surface — now wearing the Onyx
 * hex-notch silhouette (`nx-s`, the card scale). Verify in a real browser
 * (`shape()` clip + 3D transform):
 *   - the notch traces the top-right chamfer of the cover;
 *   - the `.elev-card` drop-shadow survives BOTH the notch clip-path AND the
 *     parallax tilt (no slice at the notch, no shadow dropping under the
 *     transform), and deepens to `.elev-panel` on hover;
 *   - the continuous `nx-rim` keyline rings the full silhouette including the
 *     chamfer (a real border would be sliced there).
 *
 * Hover each card to see the cursor-tracked tilt + spotlight + the staggered
 * title/description reveal — all preserved alongside the new surface.
 */

// Inline SVG data-URI covers so the demo never depends on the network.
const COVER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='480' height='270'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%230A2540'/%3E%3Cstop offset='1' stop-color='%231E6FE0'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='480' height='270' fill='url(%23g)'/%3E%3C/svg%3E";

const LAYER_BACK =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='480' height='270'%3E%3Crect width='480' height='270' fill='%23120A2E'/%3E%3C/svg%3E";
const LAYER_FRONT =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='480' height='270'%3E%3Cg fill='none' stroke='%23A855F7' stroke-width='3'%3E%3Ccircle cx='240' cy='135' r='60'/%3E%3Ccircle cx='240' cy='135' r='100'/%3E%3C/g%3E%3C/svg%3E";

export function DepthCardDemo() {
  return (
    <Section
      id="depth-card"
      title="DepthCard"
      description="A heavy 3D parallax cover card — cursor-tracked tilt, optional multi-layer depth, a cursor-following spotlight, and a staggered title/description reveal. It now wears the Onyx hex-notch silhouette (nx-s): elevation is a clip-safe .elev-card drop-shadow (lifting to .elev-panel on hover) that survives both the notch clip-path AND the parallax transform — never a box-shadow, which both would drop — and the outline is a continuous nx-rim keyline ring, never a real border (sliced at the chamfer). The 3D tilt + spotlight honor prefers-reduced-motion."
    >
      <VariantBlock
        label="Cover card"
        caption="notched surface + clip-safe drop-shadow + nx-rim keyline; hover to tilt + reveal"
      >
        <DepthCard
          image={COVER}
          title="Build your first AI Studio"
          description="A guided walkthrough — topic, content, and AI-experience setup in one flow."
          imageAlt="Studio cover"
        />
      </VariantBlock>

      <VariantBlock
        label="Multi-layer parallax"
        caption="layered covers move at different depths inside the same notched silhouette"
      >
        <DepthCard
          layers={[
            { image: LAYER_BACK, depth: 0.5 },
            { image: LAYER_FRONT, depth: 1.6 },
          ]}
          title="Deep parallax"
          description="Two cover layers drift at different rates under the cursor; the clip + keyline stay put."
          imageAlt="Layered parallax cover"
        />
      </VariantBlock>

      <VariantBlock
        label="With footer (auto height)"
        caption="footer region shares the notched silhouette + the single keyline ring (no doubled outline)"
      >
        <DepthCard
          image={COVER}
          title="Quarterly digest"
          description="Hover for the depth tilt."
          imageAlt="Digest cover"
          footer={
            <div className="flex items-center justify-between px-4 py-3 text-sm">
              <span className="text-muted-foreground">6 min read</span>
              <span className="font-medium text-foreground">Open →</span>
            </div>
          }
        />
      </VariantBlock>

      <VariantBlock
        label="No reveal animation"
        caption="revealAnimation='none' — title/description static; surface + tilt + spotlight unchanged"
      >
        <DepthCard
          image={COVER}
          title="Static labels"
          description="The reveal is off, but the depth tilt and the notch surface remain."
          revealAnimation="none"
          imageAlt="Static-label cover"
        />
      </VariantBlock>
    </Section>
  );
}
